import { useAuth0 } from "@auth0/auth0-react";
import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { PartnerService } from "../services/PartnerService";
import { BackendError, isAuthenticationError } from "../types/errors/ApiErrors";
import { IPartnerAccessContext } from "../types/partner/IPartnerAccessContext";
import { IPartnerView } from "../types/partner/IPartnerTypes";
import { PartnerApiErrorCodeEnum } from "../types/partner/PartnerEnums";

/**
 * Internal accessor used by `usePartnerAccess`. Exported only so the
 * hook file can import it without depending on a barrel.
 */
export const InternalPartnerAccessContext = createContext<IPartnerAccessContext | undefined>(undefined);

interface PartnerAccessProviderProps {
  children: ReactNode;
}

/**
 * Looks up the signed-in user's partner record once and shares it with the
 * dashboard nav, the partner route guard and the partner page.
 *
 * Only a 404 `PARTNER_NOT_FOUND` means "not a partner"; an `ENDED` partner is
 * still a partner. Authentication errors send the user to sign in again. Any
 * other failure is logged: on the first lookup it becomes `loadError`, while a
 * failed refresh keeps the partner already on screen.
 */
export const PartnerAccessProvider: React.FC<PartnerAccessProviderProps> = ({ children }) => {
  const { getAccessTokenSilently, loginWithRedirect, isAuthenticated, isLoading: isAuthLoading, user } = useAuth0();
  const signedInUserId = user?.sub;

  const [partner, setPartnerState] = useState<IPartnerView | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<Error | null>(null);
  // Only the newest lookup may write state; an older response belongs to a previous user or request.
  const latestLookupId = useRef<number>(0);
  // Read inside the lookup's catch, which must not depend on a render-time snapshot of the partner.
  const loadedPartnerRef = useRef<IPartnerView | null>(null);

  const setPartner = useCallback((nextPartner: IPartnerView | null): void => {
    loadedPartnerRef.current = nextPartner;
    setPartnerState(nextPartner);
  }, []);

  const loadPartnerAsync = useCallback(async (): Promise<void> => {
    // Bumped before the signed-out return so a response that lands after sign-out is ignored.
    latestLookupId.current += 1;
    if (isAuthLoading || !isAuthenticated) {
      return;
    }
    const lookupId = latestLookupId.current;
    const isStale = (): boolean => lookupId !== latestLookupId.current;
    try {
      const authToken = await getAccessTokenSilently();
      const meResponse = await PartnerService.getMeAsync({ authToken: authToken });
      if (isStale()) {
        return;
      }
      setPartner(meResponse.partner);
      setLoadError(null);
    } catch (err) {
      if (isStale()) {
        return;
      }
      if (isAuthenticationError(err)) {
        setPartner(null);
        await loginWithRedirect({ appState: { returnTo: window.location.pathname + window.location.search } });
        return;
      }
      const isNotPartner =
        err instanceof BackendError && err.statusCode === 404 && err.code === PartnerApiErrorCodeEnum.PARTNER_NOT_FOUND;
      if (isNotPartner) {
        setPartner(null);
        setLoadError(null);
        return;
      }
      console.error("Failed to load partner access:", err);
      if (loadedPartnerRef.current === null) {
        setLoadError(err instanceof Error ? err : new Error("Failed to load partner access"));
      }
    } finally {
      if (!isStale()) {
        setIsLoading(false);
      }
    }
  }, [getAccessTokenSilently, isAuthLoading, isAuthenticated, loginWithRedirect, setPartner]);

  useEffect(() => {
    setPartner(null);
    setLoadError(null);
    setIsLoading(true);
    void loadPartnerAsync();
    // The lookup belongs to the signed-in user, so a different user must trigger a fresh one.
  }, [loadPartnerAsync, setPartner, signedInUserId]);

  const value = useMemo<IPartnerAccessContext>(
    () => ({
      partner: partner,
      isPartner: partner !== null,
      isLoading: isLoading,
      loadError: loadError,
      refreshAsync: loadPartnerAsync,
      setPartner: setPartner,
    }),
    [partner, isLoading, loadError, loadPartnerAsync, setPartner]
  );

  return <InternalPartnerAccessContext.Provider value={value}>{children}</InternalPartnerAccessContext.Provider>;
};
